import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  useTheme,
  alpha,
  Stack,
  Fade
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TableChartIcon from '@mui/icons-material/TableChart';
import UploadFileIcon from '@mui/icons-material/UploadFile';

/**
 * Entry point component for hotel creation
 * Refined fintech-style UI with professional aesthetics
 */
const HotelCreationEntry: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleImportClick = () => {
    navigate('/add-hotel/import');
  };

  const handleManualClick = () => {
    navigate('/add-hotel/manual');
  };

  const handleBulkClick = () => {
    navigate('/add-hotel/bulk');
  };

  return (
    <Fade in timeout={600}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Header Section */}
        <Box textAlign="center" mb={8}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              px: 2,
              py: 1,
              borderRadius: 20,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <TrendingUpIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="caption" fontWeight={600} color="primary.main">
              PORTFOLIO GROWTH
            </Typography>
          </Box>

          <Typography
            variant="h3"
            gutterBottom
            fontWeight={700}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            Add New Property
          </Typography>

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 600,
              mx: 'auto',
              fontWeight: 400,
              lineHeight: 1.6
            }}
          >
            Choose your preferred method to onboard a new property
          </Typography>
        </Box>

        {/* Option Cards */}
        <Grid container spacing={3}>
          {/* Import Option */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&::before': {
                    transform: 'scaleX(1)'
                  }
                }
              }}
            >
              <CardActionArea
                onClick={handleImportClick}
                sx={{
                  height: '100%',
                  p: 4
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  {/* Icon Box */}
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2.5,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: -2,
                        borderRadius: 2.5,
                        padding: '2px',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)}, transparent)`,
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude'
                      }
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                  </Box>

                  <Typography variant="h5" gutterBottom fontWeight={700} sx={{ mb: 2 }}>
                    Smart Import
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 4,
                      lineHeight: 1.7,
                      minHeight: 64
                    }}
                  >
                    Search and auto-populate property data from public databases in seconds
                  </Typography>

                  {/* Features */}
                  <Stack spacing={1.5} mb={3}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <SpeedIcon sx={{ fontSize: 20, color: 'success.main' }} />
                      <Typography variant="body2" fontWeight={500}>
                        Quick setup (~30 seconds)
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <AutoFixHighIcon sx={{ fontSize: 20, color: 'info.main' }} />
                      <Typography variant="body2" fontWeight={500}>
                        Auto-populated property details
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Badge */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      px: 2,
                      py: 0.5,
                      borderRadius: 20,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="success.main">
                      RECOMMENDED
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          {/* Manual Entry Option */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.secondary.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                  '&::before': {
                    transform: 'scaleX(1)'
                  }
                }
              }}
            >
              <CardActionArea
                onClick={handleManualClick}
                sx={{
                  height: '100%',
                  p: 4
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  {/* Icon Box */}
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2.5,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: -2,
                        borderRadius: 2.5,
                        padding: '2px',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.3)}, transparent)`,
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude'
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: 36, color: 'secondary.main' }} />
                  </Box>

                  <Typography variant="h5" gutterBottom fontWeight={700} sx={{ mb: 2 }}>
                    Manual Entry
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 4,
                      lineHeight: 1.7,
                      minHeight: 64
                    }}
                  >
                    Build a complete property profile from scratch with our guided step-by-step wizard
                  </Typography>

                  {/* Features */}
                  <Stack spacing={1.5} mb={3}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <EditIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                      <Typography variant="body2" fontWeight={500}>
                        Complete control over data
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <AutoFixHighIcon sx={{ fontSize: 20, color: 'info.main' }} />
                      <Typography variant="body2" fontWeight={500}>
                        Step-by-step guided wizard
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Badge */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      px: 2,
                      py: 0.5,
                      borderRadius: 20,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="info.main">
                      DETAILED SETUP
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          {/* Bulk Import Option */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.success.main})`,
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.warning.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  '&::before': {
                    transform: 'scaleX(1)'
                  }
                }
              }}
            >
              <CardActionArea
                onClick={handleBulkClick}
                sx={{
                  height: '100%',
                  p: 4
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  {/* Icon Box */}
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2.5,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: -2,
                        borderRadius: 2.5,
                        padding: '2px',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.3)}, transparent)`,
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude'
                      }
                    }}
                  >
                    <TableChartIcon sx={{ fontSize: 36, color: 'warning.main' }} />
                  </Box>

                  <Typography variant="h5" gutterBottom fontWeight={700} sx={{ mb: 2 }}>
                    Bulk Import
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 4,
                      lineHeight: 1.7,
                      minHeight: 64
                    }}
                  >
                    Upload a CSV or Excel file to import multiple properties at once from existing portfolio data
                  </Typography>

                  {/* Features */}
                  <Stack spacing={1.5} mb={3}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <UploadFileIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                      <Typography variant="body2" fontWeight={500}>
                        CSV & Excel support
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <SpeedIcon sx={{ fontSize: 20, color: 'success.main' }} />
                      <Typography variant="body2" fontWeight={500}>
                        Up to 100 properties per batch
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Badge */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      px: 2,
                      py: 0.5,
                      borderRadius: 20,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="warning.main">
                      INSTITUTIONAL
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>

        {/* Footer Note */}
        <Box
          textAlign="center"
          mt={6}
          p={3}
          borderRadius={2}
          sx={{
            bgcolor: alpha(theme.palette.info.main, 0.04),
            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            You can always refine and enhance property details later from the property dashboard
          </Typography>
        </Box>
      </Container>
    </Fade>
  );
};

export default HotelCreationEntry;
